import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { setNavigate } from '@/lib/navigation';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { Loader2, X } from 'lucide-react';
import Sidebar from './Sidebar';
import SettingsSidebar from './SettingsSidebar';
import TopNav from './TopNav';
import MobileNav from './MobileNav';
import NotificationDrawer from '../common/NotificationDrawer';
import TawkChat from '../common/TawkChat';
import OfflineBanner from '../common/OfflineBanner';
import { useNotificationStore } from '@/stores/notificationStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/utils/cn';
import { ROUTES } from '@/constants/routes';
import TrialBanner from './TrialBanner';
import LockScreen from './LockScreen';

function ImportProgressBanner() {
  const navigate = useNavigate();
  const activeImportJob = useNotificationStore((s) => s.activeImportJob);
  const clearActiveImportJob = useNotificationStore((s) => s.clearActiveImportJob);
  const [dismissed, setDismissed] = useState(false);

  if (!activeImportJob || dismissed) return null;

  const isDone = activeImportJob.status === 'completed' || activeImportJob.status === 'failed';
  const isRunning = activeImportJob.status === 'pending' || activeImportJob.status === 'processing';

  return (
    <div className="flex items-center gap-3 bg-primary px-4 py-2 text-sm text-white">
      {isRunning && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
      <span className="flex-1 truncate">
        {isDone
          ? `Import ${activeImportJob.status}: ${activeImportJob.imported_count} ${activeImportJob.entityType} rows processed`
          : `Importing ${activeImportJob.entityType}… ${activeImportJob.imported_count} / ${activeImportJob.total_records} rows`}
      </span>
      <button
        type="button"
        onClick={() => navigate(ROUTES.SETTINGS_MIGRATION)}
        className="shrink-0 underline text-white/90 hover:text-white text-xs"
      >
        View Progress
      </button>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          if (isDone) clearActiveImportJob();
        }}
        className="shrink-0 text-white/70 hover:text-white"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const online = useOnlineStatus();
  const isLocked = useSubscriptionStore((s) => s.isLocked());

  const { sidebarCollapsed, collapseSidebar, expandSidebar } = useUIStore();
  const wasExpandedRef = useRef(false);

  const isSettings = location.pathname.startsWith('/dashboard/settings');

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    if (isSettings && !sidebarCollapsed) {
      wasExpandedRef.current = true;
      collapseSidebar();
    } else if (!isSettings && wasExpandedRef.current) {
      wasExpandedRef.current = false;
      expandSidebar();
    }
  }, [isSettings, sidebarCollapsed, collapseSidebar, expandSidebar]);

  return (
    <div className="flex h-screen overflow-hidden bg-app-bg">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col flex-shrink-0 transition-[width] duration-200 ease-in-out',
          sidebarCollapsed ? 'w-14' : 'w-60',
        )}
      >
        <Sidebar />
      </aside>

      {/* Settings sidebar (shown on /dashboard/settings/* routes) */}
      {isSettings && <SettingsSidebar />}

      {/* Mobile nav overlay */}
      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* Main content column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {!online && <OfflineBanner />}
        <ImportProgressBanner />
        <TrialBanner />
        <TopNav onMobileMenuOpen={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <ErrorBoundary>
            {isLocked ? <LockScreen /> : <Outlet />}
          </ErrorBoundary>
        </main>
      </div>

      {/* Notification drawer */}
      <NotificationDrawer />
      <TawkChat />
    </div>
  );
}
